// React
import { useState } from 'react'

// Mantine
import {
    Modal,
    Select,
    UnstyledButton,
    ActionIcon,
    Tooltip,
} from '@mantine/core'

import { useDisclosure } from '@mantine/hooks'
import { useListener } from 'react-bus'

// Custom hooks
import useUserLocation from '@/hooks/useUserLocation'
import { useNfcStatus } from '@/hooks/useNfcScanner'
import useNfcDiscovery from '@/hooks/useNfcDiscovery'
import { getNfcSelection } from '@/lib/nfc'

// Icons
import {
    IconPrinterOff,
    IconPrinter,
    IconCheck,
    IconSettingsPin,
    IconRefresh,
    IconMapPinFilled,
} from '@tabler/icons-react'

// Dayjs
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

// Axios API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// SWR
import { mutate } from 'swr'

// Location select
import LocationSelect from '@/components/ui/LocationSelect'

const iconProps = {
    stroke: 3,
    color: 'currentColor',
    opacity: 0.6,
    size: 16,
}

const printerSelectOption = ({ option, checked }) => {
    return (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-1">
                {checked && (
                    <IconCheck
                        style={{ marginInlineStart: 'auto' }}
                        {...iconProps}
                    />
                )}
                <span>{option.label}</span>
            </div>

            <div className="flex items-center gap-2">
                {option.at_current_location && (
                    <IconMapPinFilled size={14} color="blue" />
                )}

                {option?.state === 'offline' && (
                    <IconPrinterOff size={14} color="red" />
                )}

                {option?.state === 'online' && (
                    <IconPrinter size={14} color="#50C878" />
                )}
            </div>
        </div>
    )
}

export default function CurrentLocation() {
    // Modal
    const [opened, handlers] = useDisclosure(false)

    // Allow opening from external components via bus
    useListener('openCurrentLocation', handlers.open)

    const {
        userLocation,
        locationsSelectItems,
        userLocationId,
        onUserLocationSelect,
        printerTypes,
        userPrinters,
        userPrinterIds,
        onPrinterSelect,
        getPrintersSelectItems,
        userPrinter,
        lastPrintersCheck,
        nfcReader,
        onNfcReaderSelect,
        nfcReadersSelectItems,
    } = useUserLocation()

    const nfcStatus = useNfcStatus()
    const nfcServersStatus = useNfcDiscovery(opened)

    const renderNfcOption = ({ option, checked }) => {
        const selection = getNfcSelection(option.value)
        const status = selection && nfcServersStatus[selection.server.id]
        const detected = status?.ports.includes(selection?.port)
        const statusLabel = !status
            ? 'Recherche…'
            : status.state === 'unavailable'
                ? 'Pi inaccessible'
                : detected
                    ? 'Détecté'
                    : 'Non détecté'

        return (
            <div className="flex items-center justify-between gap-3 w-full">
                <span className="flex items-center gap-1">
                    {checked && <IconCheck {...iconProps} aria-hidden="true" />}
                    {selection ? `Port ${selection.server.ports.indexOf(selection.port) + 1}` : option.label}
                </span>
                {selection && (
                    <span
                        className={`text-xs shrink-0 ${detected ? '' : 'text-gray-500'}`}
                        style={detected ? { color: 'var(--mantine-color-green-6)' } : undefined}
                    >
                        {statusLabel}
                    </span>
                )}
            </div>
        )
    }

    // Poll printer state
    const [isPolling, setIsPolling] = useState(false)
    const { requestGet } = useAxiosApi()

    const pollPrinterState = async () => {
        setIsPolling(true)
        await requestGet('/printers/poll')
        await mutate('/locations')
        setIsPolling(false)
    }

    return (
        <>
            <UnstyledButton onClick={handlers.open}>
                <div className="flex gap-3 items-center text-sm text-gray-700 hover:bg-gray-50 h-full truncate p-3 pr-5">
                    <div className="flex flex-col truncate shrink text-right">
                        <span className="truncate text-sm">
                            {userLocation?.name}
                        </span>

                        <span className="text-gray-500 text-xs truncate">
                            {userPrinter?.name || "Pas d'imprimante"}
                        </span>
                    </div>

                    <div className="flex items-center justify-center bg-gray-100 w-10 h-10 rounded-full shrink-0">
                        <IconSettingsPin
                            className="text-gray-500"
                            size="1.3rem"
                        />
                    </div>
                </div>
            </UnstyledButton>

            <Modal
                title="Emplacement et périphériques"
                opened={opened}
                onClose={handlers.close}
            >
                <div className="flex flex-col gap-3">
                    <LocationSelect
                        label="Emplacement"
                        description="Emplacement d'affichage des stocks"
                        data={locationsSelectItems}
                        value={String(userLocationId)}
                        onChange={onUserLocationSelect}
                        allowDeselect={false}
                    />

                    {printerTypes.map(({ key, label }) => {
                        const printer = userPrinters[key]
                        const description = printer?.state === 'online'
                            ? 'En ligne'
                            : printer?.state === 'offline'
                                ? `Hors ligne ${printer.last_online_at ? dayjs(printer.last_online_at).fromNow() : ''}`
                                : undefined

                        return (
                            <Select
                                key={key}
                                label={label}
                                description={description}
                                data={getPrintersSelectItems(key)}
                                value={String(userPrinterIds[key] ?? 'none')}
                                onChange={(value) => onPrinterSelect(key, value)}
                                allowDeselect={false}
                                renderOption={printerSelectOption}
                                styles={printer?.state === 'online' ? { description: { color: 'var(--mantine-color-green-6)' } } : printer?.state === 'offline' ? { description: { color: 'var(--mantine-color-red-6)' } } : undefined}
                            />
                        )
                    })}

                    <Select
                        label="Lecteur de cartes NFC"
                        description={
                            !nfcReader
                                ? undefined
                                : nfcStatus.reader
                                    ? 'Prêt'
                                    : nfcStatus.ws
                                        ? 'Lecteur non détecté'
                                        : 'Connexion…'
                        }
                        data={nfcReadersSelectItems}
                        renderOption={renderNfcOption}
                        value={nfcReader ?? 'none'}
                        onChange={onNfcReaderSelect}
                        allowDeselect={false}
                        styles={
                            nfcReader
                                ? {
                                      description: {
                                          color: nfcStatus.reader
                                              ? 'var(--mantine-color-green-6)'
                                              : 'var(--mantine-color-orange-6)',
                                      },
                                  }
                                : undefined
                        }
                    />

                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">
                            Dernière vérification :{' '}
                            {dayjs(lastPrintersCheck).fromNow()}
                        </span>

                        <Tooltip label="Vérifier l'état des imprimantes">
                            <ActionIcon
                                variant="default"
                                size="sm"
                                loading={isPolling}
                                onClick={pollPrinterState}
                            >
                                <IconRefresh size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                </div>
            </Modal>
        </>
    )
}
